/**
 * Reports — SQL-side grouped time reporting over closed time entries.
 *
 * Mirrors the HabitStats page structure: two independent, AbortController-
 * guarded fetch domains (the grouped report for the selected dimension and
 * the per-day series for the daily chart), KPI cards, recharts, and a CSV
 * download of the current grouping. Deliberately does NOT read the
 * Analytics Reference Date — time reports use real dates.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import {
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- Types ---

type GroupBy = 'project' | 'task' | 'tag' | 'day' | 'week' | 'month';
type PresetKey = 'this_week' | 'this_month' | 'last_week' | 'last_month' | 'custom';
type SortKey = 'label' | 'seconds' | 'entries';
type SortDirection = 'asc' | 'desc';

interface ReportGroup {
  label: string;
  total_seconds: number;
  entry_count: number;
}

interface ReportSummary {
  group_by: GroupBy;
  range_start: string | null;
  range_end: string | null;
  tz_offset: number;
  total_seconds: number;
  entry_count: number;
  groups_may_overlap: boolean;
  groups: ReportGroup[];
}

const GROUP_ORDER: GroupBy[] = ['project', 'task', 'tag', 'day', 'week', 'month'];
const GROUP_LABELS: Record<GroupBy, string> = {
  project: 'Project',
  task: 'Task',
  tag: 'Tag',
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

const PRESET_ORDER: PresetKey[] = [
  'this_week',
  'this_month',
  'last_week',
  'last_month',
  'custom',
];
const PRESET_LABELS: Record<PresetKey, string> = {
  this_week: 'This week',
  this_month: 'This month',
  last_week: 'Last week',
  last_month: 'Last month',
  custom: 'Custom…',
};

// --- Helpers ---

const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours === 0 && minutes === 0) return total > 0 ? '<1m' : '0m';
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const toHours = (seconds: number): number =>
  Math.round((seconds / 3600) * 100) / 100;

interface ResolvedRange {
  start: Date | null;
  end: Date | null;
  error: string | null;
}

// Presets resolve in the browser's local timezone; weeks start on Monday to
// match the backend's ISO week buckets.
const resolveRange = (
  preset: PresetKey,
  customStart: string,
  customEnd: string,
): ResolvedRange => {
  const now = new Date();
  switch (preset) {
    case 'this_week':
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
        error: null,
      };
    case 'this_month':
      return { start: startOfMonth(now), end: endOfMonth(now), error: null };
    case 'last_week': {
      const previous = subWeeks(now, 1);
      return {
        start: startOfWeek(previous, { weekStartsOn: 1 }),
        end: endOfWeek(previous, { weekStartsOn: 1 }),
        error: null,
      };
    }
    case 'last_month': {
      const previous = subMonths(now, 1);
      return {
        start: startOfMonth(previous),
        end: endOfMonth(previous),
        error: null,
      };
    }
    default: {
      if (!customStart || !customEnd) {
        return { start: null, end: null, error: 'Choose a start and end date.' };
      }
      const start = startOfDay(parseISO(customStart));
      const end = endOfDay(parseISO(customEnd));
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return { start: null, end: null, error: 'Dates must be valid (YYYY-MM-DD).' };
      }
      if (start > end) {
        return {
          start: null,
          end: null,
          error: 'Start date must be on or before the end date.',
        };
      }
      return { start, end, error: null };
    }
  }
};

const triggerBlobDownload = (data: BlobPart, contentType: string, filename: string): void => {
  const blob = new Blob([data], { type: contentType || 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// --- Component ---

const Reports: React.FC = () => {
  const {
    cardContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const theme = useTheme();

  const [groupBy, setGroupBy] = useState<GroupBy>('project');
  const [preset, setPreset] = useState<PresetKey>('this_week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Two domains, mirroring HabitStats: the grouped report (selected
  // dimension) and the per-day series behind the daily chart. They share
  // neither loading nor error state so a flaky daily chart never blanks the
  // whole page.
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportRetryToken, setReportRetryToken] = useState(0);
  const [daily, setDaily] = useState<ReportSummary | null>(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('seconds');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Stable per-mount; timezone doesn't change mid-session.
  const tzOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  const range = useMemo(
    () => resolveRange(preset, customStart, customEnd),
    [preset, customStart, customEnd],
  );
  const rangeValid = range.error === null && !!range.start && !!range.end;

  const baseParams = useMemo(() => {
    if (!rangeValid || !range.start || !range.end) return null;
    return {
      tz_offset: tzOffset,
      from: range.start.toISOString(),
      to: range.end.toISOString(),
    };
  }, [rangeValid, range, tzOffset]);

  const fetchReport = useCallback(
    async (signal: AbortSignal) => {
      if (!baseParams) {
        // Keep the last good report; the inline error explains the pause.
        setReportLoading(false);
        return;
      }
      try {
        setReportLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/reports/summary`, {
          params: { group_by: groupBy, ...baseParams },
          signal,
        });
        if (!res.data || !Array.isArray(res.data.groups)) {
          setReportError('Report response was missing required fields.');
          setReport(null);
          return;
        }
        setReport(res.data as ReportSummary);
        setReportError(null);
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        setReportError(getApiErrorMessage(err, 'Failed to load the time report'));
      } finally {
        setReportLoading(false);
      }
    },
    [groupBy, baseParams],
  );

  const fetchDaily = useCallback(
    async (signal: AbortSignal) => {
      if (!baseParams) {
        setDailyLoading(false);
        return;
      }
      try {
        setDailyLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/reports/summary`, {
          params: { group_by: 'day', ...baseParams },
          signal,
        });
        if (!res.data || !Array.isArray(res.data.groups)) {
          setDailyError('Daily series response was missing required fields.');
          setDaily(null);
          return;
        }
        setDaily(res.data as ReportSummary);
        setDailyError(null);
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        setDailyError(getApiErrorMessage(err, 'Failed to load the daily series'));
      } finally {
        setDailyLoading(false);
      }
    },
    [baseParams],
  );

  // Independent effects, each with its own AbortController so a rapid
  // group-by/preset change can't race stale responses into state.
  useEffect(() => {
    const controller = new AbortController();
    fetchReport(controller.signal);
    return () => controller.abort();
  }, [fetchReport, reportRetryToken]);

  useEffect(() => {
    const controller = new AbortController();
    fetchDaily(controller.signal);
    return () => controller.abort();
  }, [fetchDaily, reportRetryToken]);

  const handleExport = async () => {
    if (!baseParams) return;
    try {
      setExporting(true);
      const response = await axios.get(`${API_BASE_URL}/api/reports/export.csv`, {
        params: { group_by: groupBy, ...baseParams },
        responseType: 'blob',
      });
      triggerBlobDownload(
        response.data,
        response.headers['content-type'] || 'text/csv',
        `time-report-${groupBy}.csv`,
      );
      setExportError(null);
    } catch (err: any) {
      setExportError(getApiErrorMessage(err, 'Failed to export CSV'));
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'label' ? 'asc' : 'desc');
    }
  };

  const totalSeconds = report?.total_seconds ?? 0;
  const groups = useMemo(() => report?.groups ?? [], [report]);

  const sortedGroups = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...groups].sort((a, b) => {
      if (sortKey === 'label') return factor * a.label.localeCompare(b.label);
      if (sortKey === 'entries') return factor * (a.entry_count - b.entry_count);
      return factor * (a.total_seconds - b.total_seconds);
    });
  }, [groups, sortKey, sortDirection]);

  const percentOf = (seconds: number): string =>
    totalSeconds > 0 ? `${((seconds / totalSeconds) * 100).toFixed(1)}%` : '—';

  const daysInRange = useMemo(() => {
    if (!rangeValid || !range.start || !range.end) return 0;
    return differenceInCalendarDays(range.end, range.start) + 1;
  }, [rangeValid, range]);

  const groupChartData = useMemo(
    () => groups.map((group) => ({ label: group.label, hours: toHours(group.total_seconds) })),
    [groups],
  );
  const dailyChartData = useMemo(
    () =>
      (daily?.groups ?? []).map((group) => ({
        label: group.label,
        hours: toHours(group.total_seconds),
      })),
    [daily],
  );

  const primaryColor = theme.palette.primary.main;
  const groupNoun = GROUP_LABELS[groupBy];

  const kpis = [
    { label: 'Total time', value: formatDuration(totalSeconds) },
    { label: 'Entries', value: String(report?.entry_count ?? 0) },
    { label: `${groupNoun} groups`, value: String(groups.length) },
    {
      label: 'Daily average',
      value: formatDuration(daysInRange > 0 ? totalSeconds / daysInRange : 0),
    },
  ];

  return (
    <PageShell maxWidth="wide">
      <Box sx={pageHeaderSx}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Where your tracked time went — closed entries only, grouped in SQL.
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Group by</InputLabel>
              <Select
                value={groupBy}
                label="Group by"
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              >
                {GROUP_ORDER.map((option) => (
                  <MenuItem key={option} value={option}>
                    {GROUP_LABELS[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Range</InputLabel>
              <Select
                value={preset}
                label="Range"
                onChange={(e) => setPreset(e.target.value as PresetKey)}
              >
                {PRESET_ORDER.map((option) => (
                  <MenuItem key={option} value={option}>
                    {PRESET_LABELS[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {preset === 'custom' && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <TextField
                  size="small"
                  type="date"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  error={Boolean(range.error)}
                  helperText={range.error ?? ''}
                />
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting || !rangeValid}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </Box>

      {exportError && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      {report?.groups_may_overlap && (
        <Alert severity="info" sx={sectionGapSx}>
          Entries with multiple tags count toward each of their tags, so tag
          totals can add up to more than the range total.
        </Alert>
      )}

      {reportError ? (
        <Alert
          severity="error"
          sx={sectionGapSx}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReportRetryToken((value) => value + 1)}
            >
              Retry
            </Button>
          }
        >
          {reportError}
        </Alert>
      ) : reportLoading && !report ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
            {kpis.map((kpi) => (
              <Grid item xs={12} sm={6} md={3} key={kpi.label}>
                <Card>
                  <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {kpi.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {kpi.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Hours per group */}
          <Paper sx={[panelPaddingSx, sectionGapSx]}>
            <Typography variant="h6" gutterBottom>
              Hours by {groupNoun.toLowerCase()}
            </Typography>
            {groupChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={groupChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} unit="h" />
                  <Tooltip formatter={(value: number) => [`${value} h`, 'Hours']} />
                  <Bar
                    dataKey="hours"
                    fill={primaryColor}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  No tracked time in the selected range
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Daily totals */}
          <Paper sx={[panelPaddingSx, sectionGapSx]}>
            <Typography variant="h6" gutterBottom>
              Hours per day
            </Typography>
            {dailyError ? (
              <Alert severity="error">{dailyError}</Alert>
            ) : dailyLoading && !daily ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={28} />
              </Box>
            ) : dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} unit="h" />
                  <Tooltip formatter={(value: number) => [`${value} h`, 'Hours']} />
                  <Bar
                    dataKey="hours"
                    fill={primaryColor}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  No tracked time in the selected range
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Per-group table */}
          <Paper sx={[panelPaddingSx, sectionGapSx]}>
            <Typography variant="h6" gutterBottom>
              Totals by {groupNoun.toLowerCase()}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellPaddingSx}>
                      <TableSortLabel
                        active={sortKey === 'label'}
                        direction={sortKey === 'label' ? sortDirection : 'asc'}
                        onClick={() => handleSort('label')}
                      >
                        {groupNoun}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableCellPaddingSx} align="right">
                      <TableSortLabel
                        active={sortKey === 'seconds'}
                        direction={sortKey === 'seconds' ? sortDirection : 'desc'}
                        onClick={() => handleSort('seconds')}
                      >
                        Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableCellPaddingSx} align="right">
                      <TableSortLabel
                        active={sortKey === 'entries'}
                        direction={sortKey === 'entries' ? sortDirection : 'desc'}
                        onClick={() => handleSort('entries')}
                      >
                        Entries
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={tableCellPaddingSx} align="right">
                      % of total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedGroups.map((group) => (
                    <TableRow key={group.label} hover>
                      <TableCell sx={tableCellPaddingSx}>{group.label}</TableCell>
                      <TableCell sx={tableCellPaddingSx} align="right">
                        {formatDuration(group.total_seconds)}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx} align="right">
                        {group.entry_count}
                      </TableCell>
                      <TableCell sx={tableCellPaddingSx} align="right">
                        {percentOf(group.total_seconds)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedGroups.length === 0 && (
                    <TableRow>
                      <TableCell sx={tableCellPaddingSx} colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nothing to report for this range yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </PageShell>
  );
};

export default Reports;
